import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as THREE from 'three';

const views = [
    {
        id: 'overview',
        label: 'Overview',
        title: 'Complete building',
        detail: 'Overview · Integrated systems',
        position: [7.6, 5.2, 8.4],
        target: [0, 2.05, 0],
    },
    {
        id: 'glazing',
        label: '01 Glazing',
        title: '01 · Glazing',
        detail: 'Unitised curtain wall · Spider fittings',
        position: [2.4, 2.8, 6.2],
        target: [0.4, 2.5, 1.6],
    },
    {
        id: 'cladding',
        label: '02 Cladding',
        title: '02 · Cladding',
        detail: 'Aluminium panels · Exterior skin',
        position: [7.2, 3.4, 2.6],
        target: [2.2, 2.4, 0],
    },
    {
        id: 'doors',
        label: '03 Doors & Windows',
        title: '03 · Doors & Windows',
        detail: 'Aluminium and UPVC systems',
        position: [-1.6, 1.7, 5.6],
        target: [-0.9, 1.15, 1.7],
    },
    {
        id: 'cleanroom',
        label: '04 Clean Rooms',
        title: '04 · Clean rooms',
        detail: 'Flush partitions · Walkable ceiling',
        position: [1.4, 2.2, 4.8],
        target: [0.2, 1.5, 0.2],
    },
    {
        id: 'railing',
        label: '05 Railing',
        title: '05 · Railing',
        detail: 'SS railing · Glass balustrade',
        position: [3.2, 4.4, 4.6],
        target: [0.6, 3.15, 1.5],
    },
    {
        id: 'canopy',
        label: '06 Canopies & Skylights',
        title: '06 · Canopies & Skylights',
        detail: 'Spider entrance canopy · Roof skylight',
        position: [1.2, 2.6, 6.4],
        target: [-0.4, 1.7, 1.8],
    },
];

const EnvelopeSim = ({ onChange }) => {
    const mountRef = useRef(null);
    const viewRef = useRef(views[0]);
    const onChangeRef = useRef(onChange);
    const [activeId, setActiveId] = useState('overview');
    onChangeRef.current = onChange;
    const [full, setFull] = useState(false);

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return undefined;

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf3efe8);

        const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 80);
        const look = new THREE.Vector3();
        const goal = new THREE.Vector3();
        const goalLook = new THREE.Vector3();
        camera.position.set(7.6, 5.2, 8.4);

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.8));
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        mount.appendChild(renderer.domElement);

        scene.add(new THREE.AmbientLight(0xffffff, 0.72));
        scene.add(new THREE.HemisphereLight(0xffffff, 0xe4d3b8, 0.55));
        const sun = new THREE.DirectionalLight(0xfff6ea, 1.7);
        sun.position.set(7, 12, 6);
        sun.castShadow = true;
        sun.shadow.mapSize.set(1024, 1024);
        sun.shadow.camera.near = 1;
        sun.shadow.camera.far = 28;
        sun.shadow.camera.left = -8;
        sun.shadow.camera.right = 8;
        sun.shadow.camera.top = 8;
        sun.shadow.camera.bottom = -8;
        scene.add(sun);

        const slabMat = new THREE.MeshStandardMaterial({ color: 0xe6e0d6, roughness: 0.86 });
        const frameMat = new THREE.MeshStandardMaterial({ color: 0x243040, metalness: 0.72, roughness: 0.32 });
        const glassMat = new THREE.MeshStandardMaterial({
            color: 0x6e97ae,
            metalness: 0.18,
            roughness: 0.08,
            transparent: true,
            opacity: 0.72,
        });
        const claddingMat = new THREE.MeshStandardMaterial({ color: 0xc5c1b8, metalness: 0.28, roughness: 0.46 });
        const claddingDark = new THREE.MeshStandardMaterial({ color: 0x8e8a82, metalness: 0.34, roughness: 0.42 });
        const cleanMat = new THREE.MeshStandardMaterial({ color: 0xf7f7f4, roughness: 0.9 });
        const railMat = new THREE.MeshStandardMaterial({ color: 0xd5dbe0, metalness: 0.86, roughness: 0.22 });
        const spiderMat = new THREE.MeshStandardMaterial({ color: 0xc6a56b, metalness: 0.8, roughness: 0.28 });
        const groundMat = new THREE.MeshStandardMaterial({ color: 0xe7e1d4, roughness: 0.95 });

        const ground = new THREE.Mesh(new THREE.CircleGeometry(9, 48), groundMat);
        ground.rotation.x = -Math.PI / 2;
        ground.receiveShadow = true;
        scene.add(ground);

        const building = new THREE.Group();
        scene.add(building);

        const addBox = (w, h, d, x, y, z, material, cast = true) => {
            const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), material);
            mesh.position.set(x, y, z);
            mesh.castShadow = cast;
            mesh.receiveShadow = true;
            building.add(mesh);
            return mesh;
        };

        addBox(5.1, 0.16, 4.1, 0, 0.08, 0, slabMat);
        addBox(4.5, 0.12, 3.5, 0, 2.28, 0, slabMat);
        addBox(4.5, 0.14, 3.5, 0, 4.48, 0, slabMat);

        [[-2.15, -1.65], [2.15, -1.65], [-2.15, 1.65], [2.15, 1.65]].forEach(([x, z]) => {
            addBox(0.14, 4.4, 0.14, x, 2.28, z, frameMat);
        });

        for (let row = 0; row < 4; row += 1) {
            for (let col = 0; col < 4; col += 1) {
                const paneW = 0.92;
                const paneH = 0.86;
                addBox(
                    paneW,
                    paneH,
                    0.04,
                    -1.55 + col * 1.05,
                    0.7 + row * 0.98,
                    1.78,
                    glassMat,
                    false
                );
            }
        }
        for (let index = 0; index <= 4; index += 1) {
            addBox(0.06, 4.05, 0.08, -2.08 + index * 1.05, 2.2, 1.8, frameMat);
        }
        for (let index = 0; index <= 4; index += 1) {
            addBox(4.2, 0.06, 0.08, 0.02, 0.22 + index * 0.98, 1.8, frameMat);
        }

        for (let row = 0; row < 6; row += 1) {
            for (let col = 0; col < 3; col += 1) {
                addBox(
                    0.08,
                    0.62,
                    0.95,
                    2.28,
                    0.55 + row * 0.68,
                    -1.05 + col * 1.05,
                    (row + col) % 2 === 0 ? claddingMat : claddingDark
                );
            }
        }

        addBox(0.9, 1.85, 0.06, -1.35, 1.05, 1.86, glassMat, false);
        addBox(0.08, 1.95, 0.1, -1.82, 1.05, 1.88, frameMat);
        addBox(0.08, 1.95, 0.1, -0.88, 1.05, 1.88, frameMat);
        addBox(1.05, 0.08, 0.1, -1.35, 2.02, 1.88, frameMat);

        addBox(2.2, 0.045, 1.25, -1.2, 2.16, 2.42, glassMat, false);
        addBox(2.28, 0.04, 0.05, -1.2, 2.16, 3.02, frameMat);
        const addRod = (x1, y1, z1, x2, y2, z2) => {
            const start = new THREE.Vector3(x1, y1, z1);
            const end = new THREE.Vector3(x2, y2, z2);
            const direction = end.clone().sub(start);
            const rod = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.018, direction.length(), 8), spiderMat);
            rod.position.copy(start).add(end).multiplyScalar(0.5);
            rod.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
            rod.castShadow = true;
            building.add(rod);
        };
        addRod(-1.85, 3.15, 1.82, -1.85, 2.16, 2.95);
        addRod(-0.55, 3.15, 1.82, -0.55, 2.16, 2.95);

        addBox(1.7, 0.06, 0.7, 0.85, 2.4, 2.12, slabMat);
        addBox(1.7, 0.04, 0.04, 0.85, 3.05, 2.44, railMat);
        addBox(0.04, 0.65, 0.04, 0.02, 2.72, 2.44, railMat);
        addBox(0.04, 0.65, 0.04, 1.68, 2.72, 2.44, railMat);
        addBox(0.04, 0.04, 0.7, 0.02, 3.05, 2.12, railMat);
        addBox(0.04, 0.04, 0.7, 1.68, 3.05, 2.12, railMat);

        addBox(1.5, 0.05, 1.1, 0.2, 4.62, -0.15, glassMat, false);
        addBox(1.62, 0.06, 0.06, 0.2, 4.64, 0.4, frameMat);
        addBox(1.62, 0.06, 0.06, 0.2, 4.64, -0.7, frameMat);
        addBox(0.06, 0.06, 1.16, -0.55, 4.64, -0.15, frameMat);
        addBox(0.06, 0.06, 1.16, 0.95, 4.64, -0.15, frameMat);

        addBox(1.6, 1.5, 1.4, 0.15, 1.05, 0.1, cleanMat, false);
        addBox(1.55, 0.04, 1.35, 0.15, 1.82, 0.1, cleanMat, false);

        const drag = { active: false, x: 0, y: 0, yaw: 0, pitch: 0 };
        const onDown = (event) => {
            if (event.target.closest('button')) return;
            drag.active = true;
            drag.x = event.clientX;
            drag.y = event.clientY;
        };
        const onMove = (event) => {
            if (!drag.active) return;
            drag.yaw += (event.clientX - drag.x) * 0.005;
            drag.pitch += (event.clientY - drag.y) * 0.003;
            drag.pitch = Math.max(-0.35, Math.min(0.45, drag.pitch));
            drag.x = event.clientX;
            drag.y = event.clientY;
        };
        const onUp = () => {
            drag.active = false;
        };
        mount.addEventListener('pointerdown', onDown);
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);

        const resize = () => {
            const width = mount.clientWidth;
            const height = mount.clientHeight;
            if (!width || !height) return;
            camera.aspect = width / height;
            camera.updateProjectionMatrix();
            renderer.setSize(width, height, false);
        };
        resize();
        const observer = new ResizeObserver(resize);
        observer.observe(mount);

        const clock = new THREE.Clock();
        let frame = 0;
        const render = () => {
            frame = window.requestAnimationFrame(render);
            const view = viewRef.current;
            const sway = reduced || drag.active || view.id !== 'overview' ? 0 : Math.sin(clock.getElapsedTime() * 0.35) * 0.12;
            goal.set(view.position[0], view.position[1], view.position[2]);
            goalLook.set(view.target[0], view.target[1], view.target[2]);
            const offset = goal.clone().sub(goalLook);
            offset.applyAxisAngle(new THREE.Vector3(0, 1, 0), drag.yaw + sway);
            const side = new THREE.Vector3().crossVectors(offset, new THREE.Vector3(0, 1, 0)).normalize();
            offset.addScaledVector(side, drag.pitch * 1.4);
            goal.copy(goalLook).add(offset);
            const ease = reduced ? 1 : 0.08;
            camera.position.lerp(goal, ease);
            look.lerp(goalLook, ease);
            camera.lookAt(look);
            renderer.render(scene, camera);
        };
        render();

        return () => {
            window.cancelAnimationFrame(frame);
            observer.disconnect();
            mount.removeEventListener('pointerdown', onDown);
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            renderer.dispose();
            if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
        };
    }, [full]);

    useEffect(() => {
        if (!full) return undefined;
        const onKey = (event) => {
            if (event.key === 'Escape') setFull(false);
        };
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = previous;
            window.removeEventListener('keydown', onKey);
        };
    }, [full]);

    const selectView = (view) => {
        viewRef.current = view;
        setActiveId(view.id);
        onChangeRef.current?.(view.id);
    };

    const active = views.find((view) => view.id === activeId) || views[0];

    const card = (
        <section className={`envelope-sim${full ? ' is-full' : ''}`} aria-label="Architectural envelope simulation">
            <header className="envelope-sim-bar">
                <p>Architectural envelope simulation</p>
                <span>Aluminium · DGU glass</span>
            </header>
            <div className="envelope-stage" ref={mountRef}>
                <div className="envelope-caption">
                    <strong>{active.title}</strong>
                    <span>{active.detail}</span>
                </div>
                <button type="button" className="envelope-full" onClick={() => setFull((open) => !open)}>
                    {full ? 'Close' : 'Full screen'}
                </button>
            </div>
            <div className="envelope-tabs" role="tablist" aria-label="Building systems">
                {views.map((view) => (
                    <button
                        key={view.id}
                        type="button"
                        role="tab"
                        aria-selected={view.id === activeId}
                        className={view.id === activeId ? 'is-active' : ''}
                        onClick={() => selectView(view)}
                    >
                        {view.label}
                    </button>
                ))}
            </div>
            <p className="envelope-note">Built with precision · 6063-T6 aluminium profiles · Double-glazed system</p>
        </section>
    );

    return full ? createPortal(card, document.body) : card;
};

export default EnvelopeSim;
